<script>
import * as m from '@app/paraglide/messages.js';
import {
    normalizeEventXPosition,
    normalizeEventYPosition,
    resolveEventXPosition,
} from "@app/lib/eventPosition.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

/** @type {{
 *   xPos?: string | number,
 *   yPos?: string | number,
 *   displayWidth: number,
 *   displayHeight: number,
 *   radioGroupName?: string,
 *   eventForResolve?: { text?: string },
 *   onchange?: (action: string) => void,
 *   xSelectId?: string,
 *   ySelectId?: string,
 * }} */
let {
    xPos = $bindable("center"),
    yPos = $bindable("end"),
    displayWidth,
    displayHeight,
    radioGroupName = "position",
    eventForResolve,
    onchange,
    xSelectId = "xPositionSelect",
    ySelectId = "yPositionSelect",
} = $props();

let position = $derived((yPos || "end") + (xPos || "center"));

let xDropdownValue = $derived(getDropdownValue(xPos, true));
let yDropdownValue = $derived(getDropdownValue(yPos, false));

function positionEvent() {
    return {
        ...eventForResolve,
        xPos,
        yPos,
        text: eventForResolve?.text ?? "",
    };
}

function updatePosition(pos) {
    yPos = pos.y;
    xPos = pos.x;
    onchange?.("update position");
}

async function handleXPositionChange(value) {
    if (!value || value === "current") return;

    let newXPos;
    const event = positionEvent();

    switch (value) {
        case "left":
            newXPos = "start";
            break;
        case "center":
            newXPos = "center";
            break;
        case "right":
            newXPos = "end";
            break;
        case "other": {
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.input_position_x_dialog_title(),
                text: m.input_position_x_dialog_text({ displayWidth }),
                input: "number",
                inputAttributes: {
                    min: 1,
                    max: displayWidth,
                    step: 1,
                },
                showCancelButton: true,
                confirmButtonText: m.input_position_dialog_confirm(),
                cancelButtonText: m.input_position_dialog_cancel(),
                inputValidator: (value) => {
                    const num = parseInt(value);
                    if (!value || isNaN(num) || num < 1 || num > displayWidth) {
                        return m.input_position_dialog_validator({ max: displayWidth });
                    }
                },
            });

            if (result.isConfirmed) {
                newXPos = parseInt(result.value);
            }
            break;
        }
        case "move-left":
            newXPos = normalizeEventXPosition(
                event,
                resolveEventXPosition(event, displayWidth) - 1,
                displayWidth,
            );
            break;
        case "move-right":
            newXPos = normalizeEventXPosition(
                event,
                resolveEventXPosition(event, displayWidth) + 1,
                displayWidth,
            );
            break;
        default:
            return;
    }

    if (newXPos !== undefined) {
        xPos = newXPos;
        onchange?.("update x position");
    }
}

async function handleYPositionChange(value) {
    if (!value || value === "current") return;

    let newYPos;
    const event = positionEvent();

    switch (value) {
        case "top":
            newYPos = "start";
            break;
        case "center":
            newYPos = "center";
            break;
        case "bottom":
            newYPos = "end";
            break;
        case "other": {
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.input_position_y_dialog_title(),
                text: m.input_position_y_dialog_text({ displayHeight }),
                input: "number",
                inputAttributes: {
                    min: 1,
                    max: displayHeight,
                    step: 1,
                },
                showCancelButton: true,
                confirmButtonText: m.input_position_dialog_confirm(),
                cancelButtonText: m.input_position_dialog_cancel(),
                inputValidator: (value) => {
                    const num = parseInt(value);
                    if (
                        !value ||
                        isNaN(num) ||
                        num < 1 ||
                        num > displayHeight
                    ) {
                        return m.input_position_dialog_validator({ max: displayHeight });
                    }
                },
            });

            if (result.isConfirmed) {
                newYPos = parseInt(result.value);
            }
            break;
        }
        case "move-up": {
            const currentY =
                typeof yPos === "number"
                    ? yPos
                    : yPos === "start"
                      ? 1
                      : yPos === "end"
                        ? displayHeight
                        : Math.floor(displayHeight / 2);
            newYPos = Math.max(1, currentY - 1);
            break;
        }
        case "move-down": {
            const currentYDown =
                typeof yPos === "number"
                    ? yPos
                    : yPos === "start"
                      ? 1
                      : yPos === "end"
                        ? displayHeight
                        : Math.floor(displayHeight / 2);
            newYPos = Math.min(displayHeight, currentYDown + 1);
            break;
        }
        default:
            return;
    }

    if (newYPos !== undefined) {
        yPos = normalizeEventYPosition(event, newYPos, displayHeight);
        onchange?.("update y position");
    }
}

const positions = [
    { x: "start", y: "start", title: m.input_position_top_left() },
    { x: "center", y: "start", title: m.input_position_top_center() },
    { x: "end", y: "start", title: m.input_position_top_right() },
    { x: "start", y: "center", title: m.input_position_center_left() },
    { x: "center", y: "center", title: m.input_position_center() },
    { x: "end", y: "center", title: m.input_position_center_right() },
    { x: "start", y: "end", title: m.input_position_bottom_left() },
    { x: "center", y: "end", title: m.input_position_bottom_center() },
    { x: "end", y: "end", title: m.input_position_bottom_right() },
];

function getDisplayValue(pos, isX = true) {
    if (typeof pos === "number") {
        return pos.toString();
    }
    if (isX) {
        switch (pos) {
            case "start":
                return m.input_position_display_left();
            case "center":
                return m.input_position_display_center();
            case "end":
                return m.input_position_display_right();
            default:
                return m.input_position_display_center();
        }
    } else {
        switch (pos) {
            case "start":
                return m.input_position_display_top();
            case "center":
                return m.input_position_display_center();
            case "end":
                return m.input_position_display_bottom();
            default:
                return m.input_position_display_bottom();
        }
    }
}

function getDropdownValue(pos, isX = true) {
    if (typeof pos === "number") {
        return "current";
    }

    if (isX) {
        switch (pos) {
            case "start":
                return "left";
            case "center":
                return "center";
            case "end":
                return "right";
            default:
                return "center";
        }
    } else {
        switch (pos) {
            case "start":
                return "top";
            case "center":
                return "center";
            case "end":
                return "bottom";
            default:
                return "bottom";
        }
    }
}
</script>

<div class="position-grid-and-controls">
    <div class="position-controls">
        <div class="position-grid">
            {#each positions as pos (pos.y + pos.x)}
                <label class="position-box" title={m.input_position_box_title({ position: pos.title })}>
                    <input
                        type="radio"
                        class="position-input"
                        onchange={() => updatePosition({ x: pos.x, y: pos.y })}
                        bind:group={position}
                        name={radioGroupName}
                        value={pos.y + pos.x} />
                    <span class="position-indicator"></span>
                </label>
            {/each}
        </div>
    </div>

    <div class="position-dropdowns">
        <div class="dropdown-control" title={m.input_position_x_title()}>
            <select
                id={xSelectId}
                class="form-select form-select-sm"
                bind:value={xDropdownValue}
                onchange={(e) => handleXPositionChange(e.currentTarget.value)}>
                <option value="current">{getDisplayValue(xPos, true)}</option>
                <option value="left">{m.input_position_x_left()}</option>
                <option value="center">{m.input_position_x_center()}</option>
                <option value="right">{m.input_position_x_right()}</option>
                <option value="other">{m.input_position_x_other()}</option>
                <option disabled>──────────</option>
                <option value="move-left">{m.input_position_x_move_left()}</option>
                <option value="move-right">{m.input_position_x_move_right()}</option>
            </select>
        </div>

        <div class="dropdown-control" title={m.input_position_y_title()}>
            <select
                id={ySelectId}
                class="form-select form-select-sm"
                bind:value={yDropdownValue}
                onchange={(e) => handleYPositionChange(e.currentTarget.value)}>
                <option value="current">{getDisplayValue(yPos, false)}</option>
                <option value="top">{m.input_position_y_top()}</option>
                <option value="center">{m.input_position_y_center()}</option>
                <option value="bottom">{m.input_position_y_bottom()}</option>
                <option value="other">{m.input_position_y_other()}</option>
                <option disabled>──────────</option>
                <option value="move-up">{m.input_position_y_move_up()}</option>
                <option value="move-down">{m.input_position_y_move_down()}</option>
            </select>
        </div>
    </div>
</div>

<style>
.position-grid-and-controls {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: flex-start;
}

.position-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 2px;
    width: 75px;
    height: 75px;
    background-color: rgba(0, 0, 0, 0.1);
    padding: 3px;
    border-radius: 4px;
}

.position-box {
    position: relative;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    transition: background-color 0.15s ease;
}

.position-input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
    z-index: 2;
}

.position-indicator {
    width: 70%;
    height: 70%;
    border-radius: 2px;
    transition: background-color 0.15s ease;
    background-color: transparent;
}

.position-input:checked + .position-indicator {
    background-color: #0d6efd;
}

.position-box:hover .position-indicator {
    background-color: rgba(13, 110, 253, 0.5);
}

.position-input:checked + .position-indicator {
    background-color: #0d6efd !important;
}

.position-dropdowns {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.dropdown-control {
    width: 100%;
}

.form-select {
    width: 100%;
    font-size: 12px;
}
</style>
